import { useInput } from '@capsicum/common';
import {
	type ChangeEvent,
	type FC,
	forwardRef,
	type ReactNode,
	type SVGProps,
	type SyntheticEvent,
	type TextareaHTMLAttributes
} from 'react';
import { type FieldError } from 'react-hook-form';
import styled from 'styled-components';

import { useAutoSizeTextArea } from '../../../../hooks/components/useAutoSizeTextArea';
import { useRTL } from '../../../../hooks/helpers/useRTL';

export type BaseTextareaIcon = {
	icon: FC<SVGProps<SVGSVGElement>>;
	onClick?: (event: SyntheticEvent) => void;
	testID?: string;
};

export type BaseTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
	error?: FieldError;
	hideRemainingTextLength?: boolean;
	iconEnd?: BaseTextareaIcon;
	iconStart?: BaseTextareaIcon;
	inlineIcon?: ReactNode;
	isMultiline?: boolean;
	label?: string;
	maxLength?: number;
	readonly?: boolean;
	supportingText?: ReactNode;
	testID: string;
};

/**
 *
 * @param {BaseTextareaProps} props - The component's props
 * @param {BaseTextareaIcon} props.iconStart - icon to display at the start of the input
 * @param {BaseTextareaIcon} props.iconEnd - icon to display at the end of the input
 * @param {BaseTextareaIcon} props.disabled - if the input should be disabled
 * @param {BaseTextareaIcon} props.onChange - on input's text change callback
 * @param {string} props.testID - An attribute used to identify a node for testing purposes
 */
export const BaseTextarea = forwardRef(function BaseTextarea(
	{
		className,
		disabled,
		error,
		hideRemainingTextLength,
		iconEnd,
		iconStart,
		inlineIcon,
		isMultiline = false,
		label,
		maxLength,
		onBlur,
		onChange,
		onFocus,
		placeholder,
		readonly = false,
		supportingText,
		testID,
		value,
		...props
	}: BaseTextareaProps,
	ref: React.ForwardedRef<HTMLTextAreaElement>
) {
	const {
		charLength,
		handleBlur,
		handleChange,
		handleFocus,
		hasErrorMsg,
		hasErrorState,
		inputRef,
		isFocused,
		isLabelAnimated
	} = useInput({
		error,
		maxLength,
		onBlur,
		onFocus,
		placeholder,
		ref,
		value
	});

	const { isRTL } = useRTL();

	useAutoSizeTextArea(inputRef.current, value);

	return (
		<div className={className}>
			<S.MainWrapper
				$disabled={!!disabled}
				$hasError={hasErrorState}
				$isFocused={isFocused}
				$isMultiline={isMultiline}
				onClick={() => !readonly && inputRef.current?.focus()}
			>
				{iconStart?.icon && (
					<S.IconStartWrapper $disabled={disabled} onClick={!disabled ? iconStart?.onClick : undefined}>
						<iconStart.icon height="20" width="20" />
					</S.IconStartWrapper>
				)}

				<S.InputWrapper $isReadOnly={readonly} $label={Boolean(label)}>
					{label && (
						<S.FloatingLabel $disabled={disabled} $isAnimated={isLabelAnimated} $isRTL={isRTL}>
							{label}
						</S.FloatingLabel>
					)}

					<S.TextArea
						{...props}
						$isInlineIcon={!!inlineIcon}
						$isRTL={isRTL}
						data-theme={testID}
						ref={inputRef}
						rows={1}
						value={value}
						onBlur={handleBlur}
						onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
							if (maxLength && e.target.value.length > maxLength) return;
							handleChange(e.target.value);
							onChange?.(e);
						}}
						onFocus={handleFocus}
					/>
				</S.InputWrapper>
				{inlineIcon && <S.InlineIconWrapper>{inlineIcon}</S.InlineIconWrapper>}
				{iconEnd?.icon && (
					<S.IconEndWrapper
						$disabled={disabled}
						$hasError={hasErrorState}
						$isMultiline={isMultiline}
						data-testid={iconEnd?.testID}
						onClick={!disabled ? iconEnd?.onClick : undefined}
					>
						<iconEnd.icon height="20" width="20" />
					</S.IconEndWrapper>
				)}
			</S.MainWrapper>
			<S.BottomWrapper $isRTL={isRTL}>
				{(hasErrorMsg || supportingText) && (
					<S.SupportingText $disabled={disabled} $hasError={hasErrorState}>
						{error?.message ?? supportingText}
					</S.SupportingText>
				)}
				{maxLength && !hideRemainingTextLength && (
					<S.RemainingTextLength $disabled={disabled} $hasError={hasErrorState}>
						{charLength + '/' + maxLength}
					</S.RemainingTextLength>
				)}
			</S.BottomWrapper>
		</div>
	);
});

namespace S {
	export const Container = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const MainWrapper = styled.div<{
		$disabled: boolean;
		$hasError: boolean;
		$isFocused: boolean;
		$isMultiline: boolean;
	}>`
		display: flex;
		flex-direction: row;
		position: relative;
		align-content: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		justify-content: space-between;
		min-height: ${({ theme }) => theme.sizing.xlarge};
		height: ${({ $isMultiline }) => ($isMultiline ? 'auto' : '56px')};
		padding: 0 ${({ theme }) => theme.spacings.xsmall};
		background: ${({ theme }) => theme.colors.backgrounds.main.input};
		${({ theme }) => theme.border.radius.medium};
		border: ${({ theme }) => theme.border.width.medium} solid
			${({ $hasError, $isFocused, theme }) => {
				if ($hasError) return theme.colors.borders.error.enabled;
				if ($isFocused) return theme.colors.borders.brand.enabled;

				return 'transparent';
			}};
		&:disabled {
			background-color: unset;
			border-color: unset;
		}

		&:hover {
			background-color: ${({ $disabled, theme }) =>
				!$disabled &&
				`color-mix(in srgb, ${theme.colors.stateLayers.lightNeutral.hovered['background-color']} ${
					theme.colors.stateLayers.lightNeutral.hovered.opacity * 100
				}%, ${theme.colors.backgrounds.main.input})`};
		}

		&:focus-within {
			background-color: ${({ theme }) =>
				`color-mix(in srgb, ${theme.colors.stateLayers.lightNeutral.pressed['background-color']} ${
					theme.colors.stateLayers.lightNeutral.pressed.opacity * 100
				}%, ${theme.colors.backgrounds.main.input})`};
		}
	`;

	export const InputWrapper = styled.div<{ $isReadOnly: boolean; $label: boolean }>`
		display: flex;
		flex-direction: column;
		position: relative;
		width: 100%;
		align-items: center;
		flex-grow: 2;
		margin: ${({ $label, theme }) =>
			$label ? `${theme.spacings.large} auto ${theme.spacings.xxxsmall} auto` : '0 auto'};
		pointer-events: ${({ $isReadOnly }) => ($isReadOnly ? 'none' : 'auto')};
	`;

	export const FloatingLabel = styled.label<{
		$disabled?: boolean;
		$isAnimated: boolean;
		$isRTL: boolean;
	}>`
		${({ $isAnimated, theme }) => ($isAnimated ? theme.fonts.text.caption1 : theme.fonts.text.text4)};
		position: absolute;
		${({ $isRTL }) => ($isRTL ? 'right: 0;' : 'left: 0')};
		top: -5px;
		margin: auto 0;
		text-align: ${({ $isRTL }) => ($isRTL ? 'right' : 'left')};
		color: ${({ $disabled, theme }) =>
			$disabled ? theme.colors.text.main.disabled : theme.colors.text.main.subtle};
		pointer-events: none;
		will-change: transform;
		transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
		transform-origin: left top;
		transform: ${({ $isAnimated }) => ($isAnimated ? 'translateY(-10px)' : 'translateY(0)')};
	`;

	export const TextArea = styled.textarea<{ $isInlineIcon: boolean; $isRTL: boolean; disabled?: boolean }>`
		outline: none;
		border: none;
		width: 100%;
		height: 100%;
		background: unset;
		padding: 0;
		${({ theme }) => theme.fonts.text.text3};
		text-align: ${({ $isRTL }) => ($isRTL ? 'right' : 'left')};
		caret-color: ${({ theme }) => theme.colors.borders.brand.enabled};
		color: ${({ disabled, theme }) =>
			disabled ? theme.colors.text.main.disabled : theme.colors.text.main.enabled};

		&::placeholder {
			color: ${({ disabled, theme }) =>
				disabled ? theme.colors.text.main.disabled : theme.colors.text.main.subtle};
			text-overflow: ellipsis;
		}
		resize: none;

		padding-left: ${({ $isInlineIcon, theme }) => ($isInlineIcon ? theme.spacings.medium : 'unset')};
	`;

	export const IconWrapper = styled.i<{
		$disabled?: boolean;
		$hasError?: boolean;
		$isMultiline?: boolean;
		onClick?: (e: SyntheticEvent) => void;
	}>`
		display: flex;
		justify-content: center;
		align-items: center;
		height: 100%;
		z-index: 2;
		margin: ${({ $isMultiline, theme }) => ($isMultiline ? `${theme.sizing.xxsmall} 0` : 'auto 0')};
		cursor: ${({ $disabled, onClick }) => ($disabled || !onClick ? 'default' : 'pointer')};
		pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'default')};

		& > svg {
			color: ${({ $disabled, theme }) =>
				$disabled ? theme.colors.icons.main.disabled : theme.colors.icons.main.enabled};
			width: ${({ theme }) => theme.sizing.medium};
			height: ${({ theme }) => theme.sizing.medium};
		}
	`;

	export const IconStartWrapper = styled(IconWrapper)`
		left: 0;
	`;

	export const IconEndWrapper = styled(IconWrapper)`
		right: 0;
	`;
	export const InlineIconWrapper = styled.div`
		position: absolute;
		bottom: ${({ theme }) => theme.sizing.xxxsmall};
	`;

	export const BottomWrapper = styled.div<{ $isRTL: boolean }>`
		display: flex;
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const SupportingInputInfo = styled.span<{ $disabled?: boolean; $hasError: boolean }>`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ $disabled, $hasError, theme }) => {
			if ($disabled) return theme.colors.text.main.disabled;
			if ($hasError) return theme.colors.borders.error.enabled;

			return theme.colors.text.main.enabled;
		}};
	`;

	export const SupportingText = styled(SupportingInputInfo)`
		align-self: flex-start;
		flex-grow: 2;
	`;

	export const RemainingTextLength = styled(SupportingInputInfo)`
		align-self: flex-end;
	`;
}
